import React from 'react';
import PropTypes from 'prop-types';
import { ContentCollapse } from 'site-modules/shared/components/content-collapse/content-collapse';

export function MakeValueInfoSection({ makeName = '' }) {
  return (
    <section>
      <h2 className="heading-3">A quick guide to the {makeName} value tool</h2>
      <p className="size-16">
        <span className="fw-bold">
          What is Edmunds True Market Value<sup>&reg;</sup>?
        </span>{' '}
        Edmunds True Market Value<sup>&reg;</sup> (TMV<sup>&reg;</sup>), also known as Edmunds Suggested Price, is a
        pricing system that helps you determine the average transaction price &mdash; or what others are paying &mdash;
        for new or used vehicles in your area so that you can begin your negotiations with a fair price in mind. For
        customers appraising their vehicles for sale, Edmunds TMV helps you understand how much you might expect to
        receive when you sell.
      </p>
      <p className="size-16">
        We determine those prices based on a variety of factors, including actual transaction data from dealers. CarMax
        is one of the dealers from which Edmunds obtains transaction data.
      </p>
      <p className="size-16">
        Edmunds has been a wholly owned subsidiary of CarMax, Inc. since June 2021. However, other than the
        incorporation of CarMax&apos;s transaction data by Edmunds into TMV<sup>&reg;</sup> calculations, CarMax does
        not participate in or otherwise influence the manner in which Edmunds computes those values.
      </p>

      <p className="size-16">
        <span className="fw-bold">How {makeName} values are calculated:</span> To calculate Edmunds True Market Value
        <sup>&reg;</sup> (TMV<sup>&reg;</sup>) for your {makeName}, we use data from a variety of sources, including
        dealer transactions, depreciation costs for unique vehicles, and consumer information. The appraised value is
        based on factors such as the year of the {makeName}, the model, trim, mileage, depreciation and features.
      </p>
      <p className="size-16">
        <span className="fw-bold">Which {makeName} vehicles can be appraised:</span> Just about every {makeName} is
        covered. The tool appraises vehicles dating back to 1990, so if you own a classic car, this method will not
        work. In that specific case, you&apos;d need to find a specialized classic car guide, such as Hagerty, to
        determine its fair market value.
      </p>
      <ContentCollapse
        textClosed="Read more"
        textOpen="Read less"
        justify="justify-content-start"
        btnContainerClasses="m-0 p-0"
        btnClasses="p-0 size-16 text-primary-darker more-info-btn"
        btnTextClasses="fw-normal"
        showBtnBelow
        isEclipseFade={false}
      >
        <p className="size-16">
          <span className="fw-bold">What you&apos;ll see in your {makeName} appraisal:</span> We will help you make the
          best decision if you&apos;re selling or trading in your {makeName}. You will be presented with three or four{' '}
          {makeName} automobile values: trade-in, private party, dealer retail and certified used. The trade-in price is
          what you can expect from car dealerships if you trade in your current vehicle for your next car. It is always
          the lowest of the values.
        </p>
        <p className="size-16">
          If you plan on doing a private sale, the private-party amount is what a seller can expect. This is always a
          higher amount than the trade-in value, but it takes more work because you&apos;ll be dealing with buyers
          yourself.
        </p>
        <p className="size-16">
          The dealer retail value is for used {makeName} customers. It&apos;s an average of what a shopper may expect to
          pay when they visit the {makeName} dealership to buy the pre-owned {makeName}. If your used {makeName} is new
          enough, you will also see a suggested &quot;certified used&quot; number, the approximate sale price for
          customers looking for a certified pre-owned vehicle.
        </p>
        <p className="size-16">
          <span className="fw-bold">
            What is the difference between the CarMax Offer and the Edmunds Appraisal Report?
          </span>{' '}
          Edmunds True Market Value<sup>&reg;</sup> (TMV<sup>&reg;</sup>), also known as Edmunds Suggested Price, is a
          pricing system that helps you determine the average transaction price &mdash; or what others are paying
          &mdash; for new or used vehicles in your area so that you can begin your negotiations with a fair price in
          mind. TMV values shown in the Edmunds Appraisal report are estimates, not a firm offer on your car.
        </p>

        <p className="size-16">
          Edmunds also works with CarMax to provide you with a firm offer to purchase your car for vehicles that are
          eligible for an instant offer. For those looking to sell, the CarMax offer gives you an easy way to sell your
          car directly to CarMax at your nearest CarMax location. The instant offer you receive is good for 7 days from
          the time you receive it on Edmunds.
        </p>
      </ContentCollapse>
    </section>
  );
}

MakeValueInfoSection.propTypes = {
  makeName: PropTypes.string,
};
